// Instagram Data Access via Facebook Authentication
// Uses Facebook Graph API to fetch Instagram Business Account information

/**
 * NOTE: `appSecret` was deliberately removed from this config.
 *
 * It was populated from `REACT_APP_FACEBOOK_APP_SECRET`, and every `REACT_APP_*`
 * value is inlined into the JS bundle at build time — so the Facebook app secret
 * was being shipped to every visitor's browser and sent from client-side code in
 * the two OAuth token exchanges below. An app secret must never leave the server.
 *
 * The token exchanges that needed it now throw. The supported Instagram connect
 * path is server-side: `socialConnectService.connectInstagram()` →
 * `connectInstagramAccount` mutation → the influencers Lambda, which reads the
 * secret from Secrets Manager at runtime.
 */
interface FacebookConfig {
  appId: string;
  redirectUri: string;
  scope: string[];
}

/** Thrown by the OAuth exchanges that used to run client-side with an app secret. */
const clientSideSecretRemoved = (method: string): never => {
  throw new Error(
    `FacebookInstagramService.${method} is disabled: it required a client-side ` +
      'Facebook app secret, which must never be exposed to the browser. Use the ' +
      'server-side connectInstagramAccount mutation instead.',
  );
};

interface FacebookUserData {
  id: string;
  name: string;
  email: string;
  picture?: {
    data: {
      url: string;
    };
  };
}

interface FacebookPage {
  id: string;
  name: string;
  category: string;
  access_token: string;
  instagram_business_account?: {
    id: string;
  };
}

interface InstagramBusinessAccount {
  id: string;
  username: string;
  name: string;
  biography: string;
  website: string;
  followers_count: number;
  follows_count: number;
  media_count: number;
  profile_picture_url: string;
  account_type: string; // BUSINESS or CREATOR
}

interface InstagramInsights {
  follower_count: number;
  impressions: number;
  reach: number;
  profile_views: number;
  website_clicks: number;
}

interface InstagramMediaInsights {
  engagement: number;
  impressions: number;
  reach: number;
  likes: number;
  comments: number;
  saves: number;
}

class FacebookInstagramService {
  private config: FacebookConfig;
  private baseUrl = 'https://graph.facebook.com/v18.0';

  constructor(config: FacebookConfig) {
    this.config = config;
  }

  /**
   * Generate Facebook Login URL for Instagram access
   */
  generateAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.config.appId,
      redirect_uri: this.config.redirectUri,
      scope: this.config.scope.join(','),
      response_type: 'code',
      state: this.generateState(),
    });

    return `https://www.facebook.com/v18.0/dialog/oauth?${params.toString()}`;
  }

  /**
   * Exchange Facebook authorization code for access token
   */
  async getAccessToken(_code: string): Promise<string> {
    // Was: POST /oauth/access_token with client_secret from the browser.
    return clientSideSecretRemoved('getAccessToken');
  }

  /**
   * Get long-lived Facebook access token (60 days)
   */
  async getLongLivedToken(_shortLivedToken: string): Promise<string> {
    // Was: fb_exchange_token with client_secret from the browser.
    return clientSideSecretRemoved('getLongLivedToken');
  }

  /**
   * Get Facebook user profile
   */
  async getFacebookUser(accessToken: string): Promise<FacebookUserData> {
    const fields = 'id,name,email,picture.type(large)';
    const response = await fetch(
      `${this.baseUrl}/me?fields=${fields}&access_token=${accessToken}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Facebook user: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get Facebook Pages managed by user
   */
  async getUserPages(accessToken: string): Promise<FacebookPage[]> {
    const fields = 'id,name,category,access_token,instagram_business_account';
    const response = await fetch(
      `${this.baseUrl}/me/accounts?fields=${fields}&access_token=${accessToken}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Facebook pages: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
  }

  /**
   * Get Instagram Business Account connected to Facebook Page
   */
  async getInstagramAccount(
    pageAccessToken: string,
    instagramAccountId: string
  ): Promise<InstagramBusinessAccount> {
    const fields = [
      'id',
      'username',
      'name',
      'biography',
      'website',
      'followers_count',
      'follows_count',
      'media_count',
      'profile_picture_url',
      'account_type'
    ].join(',');

    const response = await fetch(
      `${this.baseUrl}/${instagramAccountId}?fields=${fields}&access_token=${pageAccessToken}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Instagram account: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get Instagram Business Account insights
   */
  async getInstagramInsights(
    pageAccessToken: string,
    instagramAccountId: string,
    period: 'day' | 'week' | 'days_28' = 'days_28'
  ): Promise<InstagramInsights> {
    const metrics = [
      'follower_count',
      'impressions',
      'reach',
      'profile_views',
      'website_clicks'
    ].join(',');

    const response = await fetch(
      `${this.baseUrl}/${instagramAccountId}/insights?metric=${metrics}&period=${period}&access_token=${pageAccessToken}`
    );

    if (!response.ok) {
      console.warn('Instagram insights not available (may not be a Business account)');
      return {
        follower_count: 0,
        impressions: 0,
        reach: 0,
        profile_views: 0,
        website_clicks: 0
      };
    }

    const data = await response.json();

    // Transform insights data
    const insights: any = {};
    data.data.forEach((insight: any) => {
      insights[insight.name] = insight.values[0]?.value || 0;
    });

    return insights as InstagramInsights;
  }

  /**
   * Calculate engagement rate from recent Instagram media
   */
  async getInstagramEngagementRate(
    pageAccessToken: string,
    instagramAccountId: string
  ): Promise<number> {
    try {
      // Get recent media
      const mediaResponse = await fetch(
        `${this.baseUrl}/${instagramAccountId}/media?fields=id,likes_count,comments_count&limit=12&access_token=${pageAccessToken}`
      );

      if (!mediaResponse.ok) {
        console.warn('Could not fetch Instagram media for engagement calculation');
        return 0;
      }

      const mediaData = await mediaResponse.json();
      const posts = mediaData.data;

      if (!posts || posts.length === 0) {
        return 0;
      }

      // Get follower count
      const account = await this.getInstagramAccount(pageAccessToken, instagramAccountId);
      const followersCount = account.followers_count || 1;

      // Calculate average engagement
      const totalEngagement = posts.reduce((sum: number, post: any) => {
        return sum + (post.likes_count || 0) + (post.comments_count || 0);
      }, 0);

      const avgEngagement = totalEngagement / posts.length;
      const engagementRate = (avgEngagement / followersCount) * 100;

      return Math.round(engagementRate * 100) / 100; // Round to 2 decimal places
    } catch (error) {
      console.error('Error calculating Instagram engagement rate:', error);
      return 0;
    }
  }

  /**
   * Get complete Instagram data for influencer profile
   */
  async getCompleteInstagramData(accessToken: string): Promise<{
    facebookUser: FacebookUserData;
    instagramAccounts: Array<{
      page: FacebookPage;
      account: InstagramBusinessAccount;
      insights: InstagramInsights;
      engagementRate: number;
    }>;
  }> {
    try {
      // 1. Get Facebook user info
      const facebookUser = await this.getFacebookUser(accessToken);

      // 2. Get user's Facebook Pages
      const pages = await this.getUserPages(accessToken);

      // 3. Filter pages that have Instagram Business accounts
      const instagramPages = pages.filter(page => page.instagram_business_account);

      // 4. Get detailed Instagram data for each connected account
      const instagramAccounts = await Promise.all(
        instagramPages.map(async (page) => {
          try {
            const instagramAccountId = page.instagram_business_account!.id;
            const pageAccessToken = page.access_token;

            const [account, insights, engagementRate] = await Promise.all([
              this.getInstagramAccount(pageAccessToken, instagramAccountId),
              this.getInstagramInsights(pageAccessToken, instagramAccountId),
              this.getInstagramEngagementRate(pageAccessToken, instagramAccountId)
            ]);

            return {
              page,
              account,
              insights,
              engagementRate
            };
          } catch (error) {
            console.error(`Error fetching Instagram data for page ${page.name}:`, error);
            return null;
          }
        })
      );

      // Filter out failed requests
      const validInstagramAccounts = instagramAccounts.filter(Boolean) as Array<{
        page: FacebookPage;
        account: InstagramBusinessAccount;
        insights: InstagramInsights;
        engagementRate: number;
      }>;

      return {
        facebookUser,
        instagramAccounts: validInstagramAccounts
      };
    } catch (error) {
      console.error('Error fetching complete Instagram data:', error);
      throw error;
    }
  }

  /**
   * Check if user has Instagram Business account access
   */
  async hasInstagramAccess(accessToken: string): Promise<boolean> {
    try {
      const pages = await this.getUserPages(accessToken);
      return pages.some(page => page.instagram_business_account);
    } catch (error) {
      return false;
    }
  }

  /**
   * Generate secure random state for OAuth
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Validate Instagram username format
   */
  static validateUsername(username: string): boolean {
    const instagramUsernameRegex = /^[a-zA-Z0-9._]{1,30}$/;
    return instagramUsernameRegex.test(username);
  }

  /**
   * Extract username from Instagram URL
   */
  static extractUsername(url: string): string | null {
    const patterns = [
      /instagram\.com\/([^\/\?]+)/i,
      /^@?([a-zA-Z0-9._]+)$/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }
}

// Configuration for different environments
export const getFacebookConfig = (): FacebookConfig => {
  const isProduction = process.env.NODE_ENV === 'production';

  return {
    appId: process.env.REACT_APP_FACEBOOK_APP_ID || '',
    redirectUri: isProduction
      ? 'https://yourdomain.com/auth/facebook/callback'
      : 'http://localhost:3000/auth/facebook/callback',
    scope: [
      'public_profile',
      'email'
      // 'pages_show_list', // List user's Facebook Pages - Requires app review
      // 'pages_read_engagement', // Read Page content - Requires app review
      // 'instagram_basic', // Basic Instagram access - Requires app review
      // 'business_management' // For Instagram Business accounts - Requires app review
    ]
  };
};

// Create Facebook Instagram service instance
export const facebookInstagramService = new FacebookInstagramService(getFacebookConfig());

// Hook for React components
export const useInstagram = () => {
  const connectInstagram = () => {
    const authUrl = facebookInstagramService.generateAuthUrl();
    window.location.href = authUrl;
  };

  const handleCallback = async (code: string) => {
    try {
      // Get Facebook access token
      const shortToken = await facebookInstagramService.getAccessToken(code);
      const longToken = await facebookInstagramService.getLongLivedToken(shortToken);

      // Get complete Instagram data through Facebook
      const data = await facebookInstagramService.getCompleteInstagramData(longToken);

      // Store tokens securely
      localStorage.setItem('facebook_access_token', longToken);
      localStorage.setItem('instagram_connection_data', JSON.stringify(data));

      return {
        facebookUser: data.facebookUser,
        instagramAccounts: data.instagramAccounts,
        accessToken: longToken,
        // For backward compatibility, return primary Instagram account data
        profile: data.instagramAccounts[0]?.account || null,
        insights: data.instagramAccounts[0]?.insights || null,
        engagementRate: data.instagramAccounts[0]?.engagementRate || 0
      };
    } catch (error) {
      console.error('Facebook Instagram callback error:', error);
      throw error;
    }
  };

  const disconnectInstagram = () => {
    localStorage.removeItem('facebook_access_token');
    localStorage.removeItem('instagram_connection_data');
  };

  const getStoredToken = () => {
    return localStorage.getItem('facebook_access_token');
  };

  const getStoredInstagramData = () => {
    const data = localStorage.getItem('instagram_connection_data');
    return data ? JSON.parse(data) : null;
  };

  return {
    connectInstagram,
    handleCallback,
    disconnectInstagram,
    getStoredToken,
    getStoredInstagramData,
    service: facebookInstagramService
  };
};

// Legacy export for backward compatibility
export default facebookInstagramService;
